import { AppPermissionChecker } from '@auth/useApps'
import Checkbox from '@components/Checkbox'
import { colors } from '@globals/themes/defaultTheme'
import { AppEntityTypes } from '@kudos/http-client'
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Collapse from '@mui/material/Collapse'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import HelpText from '@shared/components/onboarding/HelpText'
import { AppPermission } from '@shared/permissions'
import { T } from '@transifex/react'
import type { MouseEvent } from 'react'
import { useEffect, useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router'
import { ChecklistItem as ChecklistItemType, ChecklistSubItem } from '../types/dashboard'

const ExpandIcon: React.FC<{ $expanded: boolean }> = ({ $expanded }) => {
  return (
    <ArrowForwardIosOutlinedIcon
      fontSize="small"
      sx={{
        transition: 'transform 0.2s',
        transform: $expanded ? 'rotate(-90deg)' : 'rotate(0deg)',
      }}
    />
  )
}

const cardStyle = {
  px: 1,
  py: 1.5,
  width: '100%',
  // Ensure scrolling positions the card below the fixed header
  scrollMarginTop: '120px',
}

const checkboxStyle = {
  '&.Mui-checked': {
    color: colors.greenPrimary,
  },
  '&.Mui-checked + .MuiFormControlLabel-label': {
    color: colors.greenPrimary,
  },
  '&.Mui-checked .MuiSvgIcon-root': {
    color: colors.greenPrimary,
  },
}

const ItemContainer = styled(Stack)({
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 3,
  flexDirection: 'row',
})

type ChecklistItemProps = {
  item: ChecklistItemType
  onToggle?: (itemId: string) => void
  onAlreadyOnActionUrl?: () => void
  initiallyExpanded?: boolean
  hasAppPermission?: (app: AppEntityTypes, permission: AppPermission) => boolean
}

const ChecklistItem: React.FC<ChecklistItemProps> = ({
  item,
  onToggle,
  onAlreadyOnActionUrl,
  initiallyExpanded = false,
  hasAppPermission,
}) => {
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded)
  const location = useLocation()

  // If parent later decides this item should be expanded, sync state
  useEffect(() => {
    if (initiallyExpanded) {
      setIsExpanded(true)
    }
  }, [initiallyExpanded])

  const handleToggle = () => {
    if (onToggle) {
      onToggle(item.key)
    }
  }

  const handleExpandToggle = () => {
    setIsExpanded(!isExpanded)
  }

  // Filter subItems based on visibility
  const visibleSubItems =
    item.subItems?.filter(subItem => {
      if (typeof subItem.visible === 'function') {
        return subItem.visible(hasAppPermission || (() => true))
      }
      return subItem.visible !== false
    }) || []

  const Header = ({
    item,
    isCompleted = false,
  }: {
    item: ChecklistItemType | ChecklistSubItem
    isCompleted?: boolean
  }) => {
    return (
      <>
        <Typography
          variant="body2"
          sx={{
            textDecoration: isCompleted ? 'line-through' : 'none',
          }}>
          {item.title}
        </Typography>
        {'actionUrl' in item && (
          <Typography
            variant="caption"
            sx={{
              textDecoration: isCompleted ? 'line-through' : 'none',
            }}>
            {item.description}
          </Typography>
        )}
      </>
    )
  }

  const ActionButton = (
    item: ChecklistSubItem & {
      visible?: (hasAppPermission: AppPermissionChecker) => boolean
      buttonId: string
    },
  ) => {
    if (!item.actionUrl) return null

    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
      // If already on the actionUrl, prevent navigation and notify parent
      if (location.pathname === item.actionUrl) {
        e.preventDefault()
        onAlreadyOnActionUrl?.()
      }
    }
    return (
      <Button
        id={item.buttonId}
        variant="outlined"
        sx={{ mt: 0.5, height: 48 }}
        component={RouterLink}
        to={item.actionUrl}
        state={{ fromOnboardingGuide: true }}
        onClick={handleClick}>
        <T _str="Go" />
      </Button>
    )
  }

  return (
    <Stack direction="row" alignItems="start" spacing={1} justifyContent="center">
      <Card
        id={`checklist-item-${item.key}`}
        sx={{ ...cardStyle, backgroundColor: isExpanded ? colors.whiteDark : colors.white }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Checkbox
            elementId={`checkbox-${item.key}`}
            isChecked={item.isCompleted}
            onChange={handleToggle}
            checkBoxStyle={checkboxStyle}
            label={<Header item={item} isCompleted={item.isCompleted} />}
          />
          <ItemContainer>
            <Button
              onClick={handleExpandToggle}
              id="expand-section-button"
              size="small"
              aria-label="Toggle section">
              <ExpandIcon $expanded={isExpanded} />
            </Button>
          </ItemContainer>
        </Stack>
        {/* Sub items */}
        <Collapse in={isExpanded}>
          <Box mx={1.5}>
            <Stack alignItems="flex-start">
              <Typography variant="caption">{item.description}</Typography>
            </Stack>
            {visibleSubItems.map(subItem => (
              <Card sx={{ ...cardStyle, mt: 1 }} key={subItem.key}>
                <Header item={subItem} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <ActionButton
                    key={subItem.key}
                    buttonId={`btnGo-${subItem.key}`}
                    actionUrl={subItem.actionUrl}
                    title={subItem.title}
                    description={subItem.description}
                    visible={subItem.visible}
                  />
                </Box>
              </Card>
            ))}
            {!!item.helpText && <HelpText helpText={item.helpText} mt={2} />}
          </Box>
        </Collapse>
      </Card>
    </Stack>
  )
}

export default ChecklistItem
